import { useEffect, useState } from 'react';
import { Select, SelectItem, Switch } from '@nextui-org/react';
import { Icon } from '@iconify/react';

export function EditorSection({
  step,
  title,
  description,
  children,
}: Readonly<{
  step: number;
  title: string;
  description: string;
  children: React.ReactNode;
}>) {
  return (
    <section className="rounded-xl border border-default-200 bg-content1">
      <header className="flex items-center gap-3 px-5 py-3.5 border-b border-default-200">
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs font-semibold shrink-0">
          {step}
        </span>
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
          <p className="text-[13px] text-default-500">{description}</p>
        </div>
      </header>
      <div className="flex flex-col gap-4 px-5 py-[18px]">{children}</div>
    </section>
  );
}

export function EditorField({
  label,
  htmlFor,
  required = false,
  endContent,
  className = '',
  children,
}: Readonly<{
  label: string;
  htmlFor?: string;
  required?: boolean;
  endContent?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}>) {
  return (
    <div className={`flex flex-col gap-1.5 min-w-0 ${className}`}>
      <div className="flex items-center justify-between gap-2">
        <label
          htmlFor={htmlFor}
          className="text-sm font-medium text-foreground"
        >
          {label}
          {required && <span className="text-danger"> *</span>}
        </label>
        {endContent}
      </div>
      {children}
    </div>
  );
}

export function SelectableOptionCard({
  isSelected,
  isDisabled = false,
  onSelect,
  icon,
  title,
  description,
  footer,
  tag,
}: Readonly<{
  isSelected: boolean;
  isDisabled?: boolean;
  onSelect: () => void;
  icon: string;
  title: string;
  description: string;
  footer?: string;
  tag?: string;
}>) {
  const stateClass = isSelected
    ? 'border-primary bg-primary-50 ring-1 ring-inset ring-primary'
    : 'border-default-300 bg-content1 hover:border-primary';
  return (
    <button
      type="button"
      role="radio"
      aria-checked={isSelected}
      disabled={isDisabled}
      onClick={onSelect}
      // Full name on hover when the title is clamped
      title={title}
      className={`flex flex-col gap-1 w-full min-w-0 overflow-hidden rounded-lg border px-3.5 py-3 text-left transition-colors ${stateClass} ${
        isDisabled
          ? 'opacity-45 cursor-not-allowed hover:border-default-300'
          : 'cursor-pointer'
      }`}
    >
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground w-full">
        <Icon
          icon={icon}
          className="text-primary shrink-0"
          width={18}
          height={18}
        />
        <span className="min-w-0 break-words line-clamp-2">{title}</span>
        {tag && (
          <span className="ml-auto text-[10px] font-semibold tracking-wide text-secondary border border-current rounded-lg px-1.5">
            {tag}
          </span>
        )}
      </span>
      {/* Descriptions can be unbroken keys like slm.insp.inspection_template_item; let them wrap anywhere */}
      <span className="w-full min-w-0 text-[12.5px] leading-snug text-default-500 [overflow-wrap:anywhere]">
        {description}
      </span>
      {footer && (
        <span className="mt-1 text-[11.5px] text-foreground">{footer}</span>
      )}
    </button>
  );
}

export function SettingToggle({
  title,
  description,
  isSelected,
  onValueChange,
}: Readonly<{
  title: string;
  description?: string;
  isSelected: boolean;
  onValueChange: (value: boolean) => void;
}>) {
  return (
    <div className="flex items-center justify-between gap-4 py-2.5 border-b border-default-200 last:border-b-0">
      <div className="min-w-0">
        <p className="text-sm text-foreground">{title}</p>
        {description && (
          <p className="text-xs text-default-500">{description}</p>
        )}
      </div>
      <Switch
        size="sm"
        isSelected={isSelected}
        aria-label={title}
        onValueChange={onValueChange}
      />
    </div>
  );
}

/** Single-select value from a selection change ('' when cleared) */
function getSelectedKey(keys: 'all' | Set<React.Key>): string {
  if (keys === 'all') return '';
  const [next] = keys;
  return next === undefined ? '' : next.toString();
}

export function SingleSelect({
  id,
  ariaLabel,
  options,
  value,
  onChange,
  placeholder,
  size = 'md',
}: Readonly<{
  id?: string;
  ariaLabel: string;
  options: { key: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
}>) {
  const [isOpen, setIsOpen] = useState(false);

  // This NextUI version neither repositions nor closes the open list when the page scrolls, so it
  // floats over the wrong field; close it on any scroll outside the list itself
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleScroll = (event: Event) => {
      const target = event.target;
      if (
        target instanceof Element &&
        target.closest('[role="listbox"], [data-slot="listboxWrapper"]')
      ) {
        return;
      }
      setIsOpen(false);
    };
    globalThis.addEventListener('scroll', handleScroll, true);
    return () => globalThis.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  return (
    <Select
      id={id}
      aria-label={ariaLabel}
      variant="bordered"
      size={size}
      placeholder={placeholder}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      // A value that isn't among the options (e.g. a role that no longer exists) shows the placeholder
      selectedKeys={
        value && options.some((option) => option.key === value) ? [value] : []
      }
      onSelectionChange={(keys) => onChange(getSelectedKey(keys))}
    >
      {options.map((option) => (
        <SelectItem key={option.key} value={option.key}>
          {option.label}
        </SelectItem>
      ))}
    </Select>
  );
}

export function EditorNotice({
  icon,
  children,
}: Readonly<{ icon: string; children: React.ReactNode }>) {
  return (
    <div className="flex items-start gap-2.5 rounded-lg border border-dashed border-default-300 px-3.5 py-3 text-[13px] text-default-500">
      <Icon icon={icon} className="shrink-0 mt-0.5" width={18} height={18} />
      <span>{children}</span>
    </div>
  );
}
